import { Component, EventEmitter, Input, Output, effect, forwardRef, signal, viewChild} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IUser, VisibilityName, WithFiles } from '@vierkant-software/types__api';
import { VPersonSearchDropdownComponent } from '../v-person-search/dropdown/v-person-search-dropdown.component';
import { IPersonSearch, SearchField } from '../v-person-search/component/v-person-search.base.component';

/**
 * Allows a single user selection. Once a user has been selected it will be displayed as a chip.
 * Implements ControlValueAccessor
 * 
 * @name    User Select
 * @tags    User, Input
 * @remark  Is included in the v-person-search-module
 * @link    #v-person-search|Personen Suche
 * @example 
 * ```
 * %v-user-select{[visibility]: "VisibilityName.person_search"}
 * ```
 */
@Component({
    selector:    'v-user-select',
    templateUrl: './v-user-select.component.haml',
    styles:      [".edit-button { min-height: 42px; min-width: 42px; justify-content: center;}"],
    providers:   [
        {
            provide:     NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => VUserSelectComponent),
            multi:       true
        },
    ],
    standalone: false
})
export class VUserSelectComponent implements ControlValueAccessor, IPersonSearch {
    private _user: WithFiles<IUser[]>;
    private showOverlay = signal<boolean>(false);
    /** Fallback for the chip when only an ID was written (reactive form) - v-user-list resolves it */
    private idList: string[];

    /**
     * Search value
     */
    @Input() public value: string;
    /**
     * Placeholder for the text input
     */
    @Input() public placeholder?: string;
    /**
     * Available search fields
     */
    @Input() public fields?: SearchField[];
    /**
     * Active search fields
     */
    @Input() public activeFields?: SearchField[];
    /**
     * Locked search fields
     */
    @Input() public lockedFields?: SearchField[];
    /**
     * Allow department filter.
     * @remark  Pass department IDs as string array, to limit the available filter. 
     *          Otherwise all visible departments of the current users will be listed
     * @values  string[] | true
     */
    @Input() public departments?: string[] | true;
    /**
     * Visibility of the search.
     * @values VisibilityName
     */
    @Input() public visibility?: VisibilityName;

    @Output() public valueChanged: EventEmitter<string> = new EventEmitter<string>();
    @Output() public userSelected: EventEmitter<WithFiles<IUser>> = new EventEmitter<WithFiles<IUser>>();
    public search = viewChild<VPersonSearchDropdownComponent>("search");

    public onChanged = (id: string) => this.valueChanged.emit(id);
    public onTouched = () => {};

    constructor(){
        effect(() => {
            const search = this.search();
            if (!search) return;
            if (this.showOverlay())
                this.search().showOverlay();
            else
                this.search().hideOverlay();

        });
    }

    public get user(): WithFiles<IUser[]> {
        return this._user;
    }

    /**
     * Selected user.
     */
    @Input() public set user(value: WithFiles<IUser[]>) {
        if ((!value && !this._user) ||
            (
                value?.data.length === this._user?.data.length &&
                value?.data.every(u => this.user?.data.find(x => u.ID === x.ID))
            )
        ) return;
        this._user = value;
        this.value = this._user?.data[0]?.ID;
        this.onChanged(this.value);
        if (value) {
            this.userSelected.emit({
                data:    value.data[0],
                __files: value.__files
            });
        }
    }

    public registerOnChange(fn: (id: string) => void): void {
        this.onChanged = (id) => {
            fn?.(id);
            this.valueChanged.emit(id);
        };
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public selectUser(ids: string[]) {
        const id = ids.shift();
        if ([null, undefined].includes(id) || id === this.value) return;

        const index = this.search().searchResult.findIndex(x => x.ID === id);
        this.user = {
            data:    [{...this.search().searchResult.at(index)}],
            __files: [this.search().avatars.at(index)]
        };
        this.showOverlay.set(false);
    }

    public unselectUser() {
        // not via the `user` setter: with an ID-only value `_user` is already null and the setter would return early
        this._user = null;
        this.idList = undefined;
        this.value = undefined;
        this.onChanged(this.value);
        this.showOverlay.set(true);
    }

    /** What the chip shows: the selected user, or the written ID resolved by v-user-list */
    public get chipValue(): WithFiles<IUser[]> | string[] {
        return this._user ?? this.idList;
    }

    public writeValue(id: string): void {
        this.value = id;
        if (!id || this._user?.data[0]?.ID !== id)this._user = null;
        this.idList = id ? [id] : undefined;
    }
}
